import { Flex, Tag, Tooltip } from 'antd'
import { LucideIcon, type LucideIconName } from 'components/LucideIcon'
import type {
  Pill,
  PillCategories,
  PillGroup,
} from 'features/libra/dashboard/ticketDashboard/types'
import type { FC } from 'react'
import { memo } from 'react'
import styled from 'styled-components'

import { TicketPill } from './TicketPill'

/** Tag takes its height from text; with only an icon it must centre the icon itself. */
const CapTag = styled(Tag)`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
`

/**
 * One group: rounded outer corners only, with a small gap so every pill keeps
 * its full outline even when they look alike. It never wraps, since that would
 * break the corners; when wider than its container the pills shrink and
 * ellipsize instead, and each pill's tooltip still has the full name.
 */
const JoinedPills = styled.span`
  display: inline-flex;
  gap: 1px;
  min-width: 0;
  max-width: 100%;

  & > .ant-tag {
    margin: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & > .ant-tag:not(:first-child) {
    border-start-start-radius: 0;
    border-end-start-radius: 0;
  }

  & > .ant-tag:not(:last-child) {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
  }
`

interface PillListProps {
  pills: ReadonlyArray<Pill>
  categories: PillCategories
}

/** The category's group, else the category as a group of its own. */
function groupOf(category: string, categories: PillCategories): PillGroup {
  const def = categories[category]
  return (
    def?.group ?? {
      id: category,
      label: def?.label ?? category,
      icon: def?.icon,
    }
  )
}

/** Pills grouped for display, each group where its first pill appears. */
function groupPills(
  pills: ReadonlyArray<Pill>,
  categories: PillCategories
): { group: PillGroup; pills: Pill[] }[] {
  const groups = new Map<string, { group: PillGroup; pills: Pill[] }>()
  for (const pill of pills) {
    const group = groupOf(pill.category, categories)
    const entry = groups.get(group.id) ?? { group, pills: [] }
    entry.pills.push(pill)
    groups.set(group.id, entry)
  }
  return [...groups.values()]
}

function renderPill(pill: Pill) {
  return <TicketPill key={`${pill.category}:${pill.value}`} pill={pill} />
}

/**
 * Several pills from one group (e.g. each sign-off slot) render as one joined
 * group. A group with an icon always joins, led by a neutral cap showing it.
 */
const RawPillList: FC<PillListProps> = ({ pills, categories }) => (
  <Flex wrap gap='small' align='center'>
    {groupPills(pills, categories).map(({ group, pills: groupedPills }) => {
      if (groupedPills.length < 2 && !group.icon) {
        return groupedPills.map(renderPill)
      }
      return (
        <JoinedPills key={group.id}>
          {group.icon && <GroupCap icon={group.icon} label={group.label} />}
          {groupedPills.map(renderPill)}
        </JoinedPills>
      )
    })}
  </Flex>
)

export const PillList = memo(RawPillList)

const ICON_SIZE = 12

function GroupCap({ icon, label }: { icon: LucideIconName; label: string }) {
  return (
    <Tooltip title={label}>
      <CapTag>
        <LucideIcon name={icon} size={ICON_SIZE} />
      </CapTag>
    </Tooltip>
  )
}
